import { useMemo } from 'react';
import { Box, Chip, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getDetectionColor } from '../Utilities';
import { isMobile, useMobileOrientation } from 'react-device-detect';

export default function DetectionFilters({ objectTypes, activeTypes, enabledTypes, typeTotals, onToggle, onSolo, onAll, onNone, translateType, totalDetections, imageCount }) {

    const { t } = useTranslation()
    const { isPortrait } = useMobileOrientation()

    const theme = useTheme()
    const is2k = useMediaQuery('(min-width:1920px)')
    const isXxl = useMediaQuery('(min-width:1700px)')
    const isXl = useMediaQuery(theme.breakpoints.up('xl'))
    const isLg = useMediaQuery(theme.breakpoints.up('lg'))
    const isMd = useMediaQuery(theme.breakpoints.up('md'))
    const isSm = useMediaQuery(theme.breakpoints.up('sm'))
    const isXs = useMediaQuery('(min-width:480px)')
    const isXxs = useMediaQuery('(min-width:340px)')
    const cols = is2k ? 8 : isXxl ? 7 : isXl ? 6 : isLg ? 5 : isMd ? 3 : isSm ? 3 : isXs ? 2 : isXxs ? 2 : 1
    const chipHeight = isMobile ? 22 : 24
    const avatarSize = isMobile ? 14 : 18
    const gap = 6 // 0.75 * 8
    const maxRows = isMobile ? 3 : 4
    const maxHeight = maxRows * chipHeight + (maxRows - 1) * gap

    const typesWithData = useMemo(() => new Set(activeTypes || objectTypes), [activeTypes, objectTypes])
    const allActive = objectTypes.length > 0 && [...typesWithData].every(elem => enabledTypes.has(elem))
    const sortedTypes = useMemo(() => [...objectTypes].sort((a, b) => translateType(a).localeCompare(translateType(b))), [objectTypes, translateType])
    const rows = Math.ceil(sortedTypes.length / cols)

    return (
        <Box sx={{ mb: isMobile ? 1 : 2, p: 1.5, bgcolor: 'white', borderRadius: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1 : 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, flexGrow: 1, ml: 0.5 }}>
                    {t('pages.dashboard.object_types')}
                </Typography>
                {totalDetections !== undefined && imageCount !== undefined && !(isMobile && isPortrait) && (
                    <Typography variant="caption" color="text.secondary">
                        {imageCount} {t('pages.dashboard.images')} - {totalDetections} {t('pages.dashboard.total_detections')}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    size="small"
                    disabled={allActive}
                    onClick={onAll}
                    sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12 }}
                >
                    {t('pages.dashboard.all')}
                </Button>
                <Button variant="outlined" size="small" disabled={enabledTypes.size === 0} onClick={onNone} sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12 }}>
                    {t('pages.dashboard.none')}
                </Button>
            </Box>
            <Box sx={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridTemplateRows: `repeat(${rows}, auto)`,
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 0.75,
                maxHeight,
                overflowY: 'auto'
            }}>
                {sortedTypes.map(elem => {
                    const displayName = translateType(elem)
                    const count = typeTotals[elem] || 0
                    const hasData = typesWithData.has(elem)
                    const active = enabledTypes.has(elem) && hasData

                    return (
                        <Chip
                            key={elem}
                            size="small"
                            avatar={
                                <Box sx={{ borderRadius: '50%', bgcolor: getDetectionColor(elem) }} />
                            }
                            label={`${displayName} (${count})`}
                            variant={active ? 'filled' : 'outlined'}
                            onClick={() => hasData && onToggle(elem)}
                            onDoubleClick={() => hasData && onSolo(elem)}
                            sx={{
                                height: chipHeight,
                                fontSize: isMobile ? 12 : undefined,
                                cursor: hasData ? 'pointer' : 'default',
                                justifyContent: 'flex-start',
                                overflow: 'hidden',
                                opacity: hasData ? 1 : 0.4,
                                '& .MuiChip-avatar': { width: avatarSize, height: avatarSize, minWidth: avatarSize, ml: '4px' },
                                '& .MuiChip-label': { pl: '8px' },
                                ...(active && {
                                    bgcolor: '#e6ecf3',
                                    borderColor: '#003366'
                                })
                            }}
                        />
                    )
                })}
            </Box>
        </Box>
    )
}